import { Suspense, use } from "react";
import { DemoCard, summarizePage, type PageSummary } from "../components/DemoCard";
import { loadPageTree, type Scenario } from "../lib/capture-loader";
import { ProvenanceFooter } from "../components/ProvenanceFooter";

const SCENARIOS: Scenario[] = ["storefront", "product-detail", "checkout"];

interface AllSummaries {
  storefront: PageSummary;
  "product-detail": PageSummary;
  checkout: PageSummary;
}

let allSummariesPromise: Promise<AllSummaries> | null = null;
function loadAllSummaries(): Promise<AllSummaries> {
  if (!allSummariesPromise) {
    allSummariesPromise = Promise.all(SCENARIOS.map((s) => loadPageTree(s))).then(
      ([storefront, productDetail, checkout]) => ({
        storefront: summarizePage(storefront),
        "product-detail": summarizePage(productDetail),
        checkout: summarizePage(checkout),
      }),
    );
  }
  return allSummariesPromise;
}

function LandingInner({ onNavigate }: { onNavigate: (p: string) => void }) {
  const all = use(loadAllSummaries());
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
      {SCENARIOS.map((s) => (
        <DemoCard key={s} scenario={s} summary={all[s]} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

export function Landing({ onNavigate }: { onNavigate: (p: string) => void }) {
  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden font-sans selection:bg-indigo-500/30">
      {/* Background neon blur orbs */}
      <div className="absolute top-[-200px] left-[-200px] w-[500px] h-[500px] rounded-full bg-indigo-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-100px] right-[-100px] w-[600px] h-[600px] rounded-full bg-purple-500/10 blur-[150px] pointer-events-none" />

      <main className="max-w-6xl mx-auto px-6 py-16 relative z-10">
        {/* Top bar header */}
        <header className="flex justify-between items-center mb-20 border-b border-white/5 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-inner" />
            <span className="font-headline font-bold tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              COGO STUDIO
            </span>
          </div>
          <span className="px-2.5 py-1 bg-white/5 border border-white/10 text-[10px] font-mono text-slate-400 rounded-lg">
            v77.0 Edge Runtime
          </span>
        </header>

        {/* Hero Section */}
        <section className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 text-xs font-bold tracking-widest text-indigo-400 rounded-full mb-6 font-headline">
            AI NO-CODE PLATFORM
          </span>
          <h1 className="text-4xl sm:text-5xl font-headline tracking-tight text-white mb-6 leading-tight">
            Instant High-Fidelity App Generation
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-8 font-sans">
            Cogo maps natural language intents to strict 3-layer schemas, generating gorgeous,
            fully functional glassmorphism applications rendered instantly without standard build steps.
          </p>
        </section>

        {/* Dynamic page grids */}
        <Suspense
          fallback={
            <div className="text-center py-20 text-slate-500 font-mono">
              Loading synthesized page trees…
            </div>
          }
        >
          <LandingInner onNavigate={onNavigate} />
        </Suspense>

        <ProvenanceFooter />
      </main>
    </div>
  );
}
